/**
 * imageCompression.ts
 * 
 * Compresses image files using the Canvas API before upload.
 * - Resizes large images to max 1920×1920
 * - Compresses JPEG to ~82% quality
 * - Preserves PNG transparency
 * - Non-image files (PDFs, docs) pass through unchanged
 * - Falls back to original file if anything goes wrong
 */

interface CompressOptions {
  /** Max width in pixels. Default: 1920 */
  maxWidth?: number;
  /** Max height in pixels. Default: 1920 */
  maxHeight?: number;
  /** JPEG quality 0–1. Default: 0.82 */
  quality?: number;
  /** Skip compression if file is already smaller than this (bytes). Default: 200 KB */
  skipIfSmallerThan?: number;
}

const DEFAULT_OPTS: Required<CompressOptions> = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.82,
  skipIfSmallerThan: 200 * 1024, // 200 KB
};

/**
 * Compress a single image File.
 * Returns a new File with reduced size, or the original if compression
 * is not possible / would make the file larger.
 */
export async function compressImage(file: File, opts?: CompressOptions): Promise<File> {
  const { maxWidth, maxHeight, quality, skipIfSmallerThan } = { ...DEFAULT_OPTS, ...opts };

  // Only compress raster images
  const compressableMimes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/bmp'];
  if (!compressableMimes.includes(file.type)) return file;

  // Skip tiny files — compression overhead isn't worth it
  if (file.size <= skipIfSmallerThan) return file;

  return new Promise<File>((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(file); // fallback on error
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const img = new Image();
      img.onerror = () => resolve(file); // fallback on error
      img.onload = () => {
        let { width, height } = img;

        // Scale down proportionally if needed
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(file); return; }

        // White background for JPEG (no transparency)
        if (file.type !== 'image/png') {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(img, 0, 0, width, height);

        // Use PNG for PNGs (preserve transparency), JPEG for everything else
        const outputMime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const outputQuality = outputMime === 'image/jpeg' ? quality : undefined;

        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(file); return; }
            // If compressed file is actually larger, keep the original
            if (blob.size >= file.size) { resolve(file); return; }
            // Keep the original extension in the filename
            const compressed = new File([blob], file.name, {
              type: outputMime,
              lastModified: Date.now(),
            });
            resolve(compressed);
          },
          outputMime,
          outputQuality,
        );
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Compress an array of image Files in parallel.
 * Non-image files pass through unchanged.
 */
export async function compressImages(files: File[], opts?: CompressOptions): Promise<File[]> {
  return Promise.all(files.map((f) => compressImage(f, opts)));
}

/** Quick size formatter for debug logging */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
